import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Globe, FileText, Brain, ExternalLink, CheckCircle } from "lucide-react";
import CustomPromptInput from "./CustomPromptInput";

interface WebContentGeneratorProps {
  onContentGenerated: (content: any) => void;
  generationType: "mcq" | "note";
}

interface ExtractedContent {
  title: string;
  content: string;
  url: string;
  wordCount: number;
  summary: string;
}

export default function WebContentGenerator({ 
  onContentGenerated, 
  generationType 
}: WebContentGeneratorProps) {
  const { toast } = useToast();
  const [url, setUrl] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [extractedContent, setExtractedContent] = useState<ExtractedContent | null>(null);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);

  const extractMutation = useMutation({
    mutationFn: async (sourceUrl: string) => {
      const { data, error } = await supabase.functions.invoke('ai-web-scraper', {
        body: { url: sourceUrl }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setExtractedContent(data);
      setSelectedSections([data.content]); // Select all content by default
      toast({
        title: "Content Extracted Successfully",
        description: `Extracted ${data.wordCount} words from the webpage`,
      });
    },
    onError: (error) => {
      toast({
        title: "Extraction Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!extractedContent) throw new Error("No content extracted");
      
      const selectedContent = selectedSections.join("\n\n");
      const functionName = generationType === "mcq" ? "ai-generate-mcqs" : "ai-note-assistant";
      
      if (generationType === "mcq") {
        const { data, error } = await supabase.functions.invoke(functionName, {
          body: {
            topic: extractedContent.title,
            difficulty: 3,
            count: 5,
            customPrompt,
            sourceUrl: extractedContent.url,
            webContent: selectedContent
          }
        });
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase.functions.invoke(functionName, {
          body: {
            action: "generate",
            content: selectedContent,
            title: extractedContent.title,
            customPrompt,
            sourceUrl: extractedContent.url,
            webContent: selectedContent
          }
        });
        if (error) throw error;
        return data;
      }
    },
    onSuccess: (data) => {
      onContentGenerated({
        ...data,
        sourceUrl: extractedContent?.url,
        sourceTitle: extractedContent?.title
      });
      toast({
        title: `${generationType.toUpperCase()} Generated Successfully`,
        description: `Generated from web content using ${data.metadata?.tokensUsed || data.tokensUsed || 0} tokens`,
      });
    },
    onError: (error) => {
      toast({
        title: "Generation Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleExtractContent = () => {
    if (!url.trim()) {
      toast({
        title: "URL Required",
        description: "Please enter a valid URL to extract content",
        variant: "destructive",
      });
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid URL (e.g., https://example.com)",
        variant: "destructive",
      });
      return;
    }

    extractMutation.mutate(url);
  };

  const handleGenerate = () => {
    if (!extractedContent) {
      toast({
        title: "No Content",
        description: "Please extract content from a URL first",
        variant: "destructive",
      });
      return;
    }

    if (selectedSections.length === 0) {
      toast({
        title: "No Content Selected",
        description: "Please select at least one section of content",
        variant: "destructive",
      });
      return;
    }

    generateMutation.mutate();
  };

  const isValidUrl = (urlString: string) => {
    try {
      new URL(urlString);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Web Content Extraction
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="source-url">Source URL *</Label>
            <div className="flex gap-2">
              <Input
                id="source-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/medical-article"
                className="border-primary/20"
              />
              <Button 
                onClick={handleExtractContent}
                disabled={extractMutation.isPending || !url.trim()}
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              >
                {extractMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    Extract
                  </>
                )}
              </Button>
            </div>
            {url && !isValidUrl(url) && (
              <p className="text-sm text-destructive">Please enter a valid URL</p>
            )}
          </div>

          {extractedContent && (
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h3 className="font-medium text-sm">{extractedContent.title}</h3>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <ExternalLink className="h-3 w-3" />
                    <span className="truncate max-w-md">{extractedContent.url}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {extractedContent.wordCount} words extracted
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Content Summary:</Label>
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded">
                  {extractedContent.summary}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  <CheckCircle className="h-4 w-4 inline mr-1 text-success" />
                  Content Selected for Processing
                </Label>
                <Textarea
                  value={selectedSections.join("\n\n")}
                  onChange={(e) => setSelectedSections([e.target.value])}
                  rows={6}
                  className="text-sm border-primary/20"
                  placeholder="Edit or modify the extracted content before processing..."
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CustomPromptInput
        value={customPrompt}
        onChange={setCustomPrompt}
        contentType={generationType}
        label={`Custom Instructions for ${generationType.toUpperCase()} Generation`}
        placeholder={`Add specific instructions for generating ${generationType}s from this web content...`}
      />

      {extractedContent && (
        <Card>
          <CardContent className="pt-6">
            <Button 
              onClick={handleGenerate}
              disabled={generateMutation.isPending || selectedSections.length === 0}
              className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating {generationType.toUpperCase()}s...
                </>
              ) : (
                <>
                  <Brain className="mr-2 h-4 w-4" />
                  Generate {generationType.toUpperCase()}s from Web Content
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}